import React, { useEffect, useState } from 'react'
import { Box, Grid, Stack, Typography, Card, IconButton, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// icons
import AngleRightIcon from "common/customIcons/angle_right";
// utils and helpers
import { grey } from "@mui/material/colors";
import { formattedDate, formatTimestampToWithoutTime, formatTimeStampToDate } from "utilities/common";
import { approveClosureForm, getClosureFormReport } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import { UploadPhoto } from "common/uploadPhoto";

const ClosureFormReport = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { distributionId } = useParams()
  const [searchParams] = useSearchParams()
  const { driver, region, subRegion, date } = Object.fromEntries(searchParams.entries())

  const { user = {} } = useSelector((state) => state.auth)

  const [details, setDetails] = useState({});
  const [additionalNotes, setAdditionalNotes] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleClosureFormDetails = async () => {
    try {
      const request = { distributionId, driverId: driver, regionId: region, subRegionId: subRegion }
      const formattedDate = formatTimeStampToDate(Number(date));
      if (formattedDate !== "Invalid date") {
        request.date = formattedDate;
      }
      const response = await dispatch(getClosureFormReport(request)).unwrap();
      const { data } = response || {};
      if (data) {
        setDetails(data || {});
      }
    } catch (error) {
      showToastMsg(error, "error");
    }
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    setAdditionalNotes(prev => ({ ...prev, [name]: value }));
  };

  const handleDeletePhoto = (image) => {
    setAdditionalNotes(prev => ({ ...prev, additionalPhoto: prev?.additionalPhoto?.filter((img) => img !== image) }))
  }

  const handleSubmit = async () => {
    if (isLoading) return;
    try {
      setIsLoading(true);

      const request = {
        distributionId,
        regionId: region,
        subRegionId: subRegion,
        date: formattedDate(details.date),
        driverId: driver,
        closedBy: user?.name || "",
        ...additionalNotes
      };
      const response = await dispatch(approveClosureForm(request)).unwrap();

      const { data } = response || {};
      if (data) {
        navigate("/hyperwolf/closure-forms")
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (distributionId && driver && region && subRegion) {
      handleClosureFormDetails();
    }
  }, [distributionId, driver, region, subRegion])

  return (
    <CommonLayout>
      <Box p={4} className="cus-contents">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Closure Form ({details?.date ? formatTimestampToWithoutTime(details?.date || "") : ""})
            </Typography>
          </Box>
        </Stack>

        {/* Top section */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2.5, boxShadow: "none" }}>
          <Grid container alignItems="center" rowSpacing={2} columnSpacing={2}>
            {/* Top details */}
            <Grid item xs={12} sm={6}>
              <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Closure Form Report</Typography>
            </Grid>

            {/* Action buttons */}
            <Grid item xs={12} sm={6}>
              <Stack direction='row' gap={1} alignItems='center' justifyContent="end">
                <div className="d-flex align-item-top gap-2">
                  <MDButton
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: "160px" }}
                    disabled={isLoading}
                    onClick={handleSubmit}
                  >
                    {isLoading ? <CircularProgress size={22} color="light" /> : "Submit"}
                  </MDButton>
                </div>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ p: { xs: 3, md: 4 }, borderRadius: 2.5, boxShadow: "none" }}>
          {/* Quantity data */}
          <Stack direction='row' gap={3} alignItems='start' justifyContent="space-between" style={{ width: "100%" }}>
            <div className="w-100 d-grid grid-columns-four gap-3">
              <div className="d-flex align-item-top flex-column gap-1">
                <Typography variant='body2' fontSize="12px" color="#656575">Expected Products</Typography>
                <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.expectedProducts}</Typography>
              </div>
              <div className="d-flex align-item-top flex-column gap-1">
                <Typography variant='body2' fontSize="12px" color="#656575">Total Discrepancy</Typography>
                <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.totalDiscrepancy}</Typography>
              </div>
              <div className="d-flex align-item-top flex-column gap-1">
                <Typography variant='body2' fontSize="12px" color="#656575">Expected Revenue from Credit Card</Typography>
                <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.expectedRevenueCredit}</Typography>
              </div>
              <div className="d-flex align-item-top flex-column gap-1">
                <Typography variant='body2' fontSize="12px" color="#656575">Actual Revenue from Credit Card</Typography>
                <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.actualRevenueCredit}</Typography>
              </div>
              <div className="d-flex align-item-top flex-column gap-1">
                <Typography variant='body2' fontSize="12px" color="#656575">Expected Revenue from Cash</Typography>
                <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.expectedRevenueCash}</Typography>
              </div>
              <div className="d-flex align-item-top flex-column gap-1">
                <Typography variant='body2' fontSize="12px" color="#656575">Actual Revenue from Cash</Typography>
                <Typography variant='h6' color="#2E2E3A" fontWeight="600">${details?.actualRevenueCash}</Typography>
              </div>
              <div className="d-flex align-item-top flex-column gap-1">
                <Typography variant='body2' fontSize="12px" color="#656575">Closure Name</Typography>
                <Typography variant='h6' color="#2E2E3A" fontWeight="600">{details?.closureName}</Typography>
              </div>
            </div>
          </Stack>

          <Grid container alignItems="start" spacing={2} mt={0}>
            {/* Additional notes */}
            <Grid item xs={12}>
              <hr className='my-2' style={{ borderTop: "1px solid #D0D0DA", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant='h4' fontWeight="semibold" style={{ fontSize: "20px" }}>Additional Discrepancy Note</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Discrepancy Note</Typography>
              <textarea
                placeholder='Add Note...'
                style={{
                  width: "100%", minHeight: "130px", borderRadius: "20px", boxShadow: 'none', border: "1px solid #9A9AAF", overflow: "hidden", outline: "none", padding: "12px 20px", resize: "none"
                }}
                name="additionalNote"
                value={additionalNotes.additionalNote || ""}
                onChange={handleChange}
              />
            </Grid>

            {/* Upload file */}
            <Grid item xs={12} md={6}>
              <UploadPhoto
                photos={additionalNotes.additionalPhoto}
                handleDeletePhoto={handleDeletePhoto}
                updatePhotos={photo => setAdditionalNotes(prev => ({ ...prev, additionalPhoto: [...(prev.additionalPhoto || []), photo] }))}
              />
            </Grid>
          </Grid>

        </Card>
      </Box>
    </CommonLayout>
  )
}

export default ClosureFormReport
